import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, CreditCard, FileText } from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  LicensePlate,
  Pagination,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from "@rumbledirect/ui-v2";
import type {
  ChargingCardAssignment,
  ChargingContract,
} from "@/charging/charging";
import {
  assignmentTypeLabel,
  contractStatusLabel,
  contractTypeLabel,
  formatTariffPerKwh,
} from "@/charging/chargingLabels";
import { DetailSection, FieldGrid } from "@/dossiers/DetailSection";
import {
  useGetAdminChargingCardAssignments,
  useGetAdminChargingContractsId,
} from "@/lib/api/generated/charging/charging";
import { EMPTY_VALUE, formatDateOrEmpty } from "@rumbledirect/ui-v2/format";

const ITEMS_PER_PAGE = 15;
const COLUMN_COUNT = 5;
const SKELETON_ROW_KEYS = Array.from(
  { length: 3 },
  (_, index) => `charging-assignment-skeleton-${index}`,
);

interface ContractDetailSearch {
  readonly page: number;
}

export const Route = createFileRoute(
  "/_authenticated/charging-contracts_/$contractId",
)({
  validateSearch: (search: Record<string, unknown>): ContractDetailSearch => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page >= 1 ? page : 1 };
  },
  component: ChargingContractDetailPage,
});

function ChargingContractDetailPage(): ReactNode {
  const { contractId } = Route.useParams();
  const { data, isPending, isError, refetch } =
    useGetAdminChargingContractsId(contractId);

  return (
    <div className="space-y-4">
      <Link
        to="/charging-contracts"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar laadcontracten
      </Link>

      {isError ? (
        <Card>
          <CardContent className="py-10 text-center">
            <div className="space-y-3" role="alert">
              <p className="text-muted-foreground">
                We konden dit laadcontract niet laden.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Opnieuw proberen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <ContractHeader contract={data} isPending={isPending} />
          <ContractFields contract={data} isPending={isPending} />
          <AssignmentsSection contractId={contractId} />
        </>
      )}
    </div>
  );
}

function ContractHeader({
  contract,
  isPending,
}: {
  readonly contract: ChargingContract | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !contract) {
    return <Skeleton className="h-8 w-64" />;
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-2xl font-semibold tracking-tight">
        {contract.providerName}
      </h1>
      <Badge
        variant={
          contract.status === "active"
            ? "success"
            : contract.status === "suspended"
              ? "warning"
              : "neutral"
        }
        size="sm"
      >
        {contractStatusLabel(contract.status)}
      </Badge>
      <span className="text-sm text-muted-foreground">
        {contractTypeLabel(contract.contractType)}
      </span>
    </div>
  );
}

function ContractFields({
  contract,
  isPending,
}: {
  readonly contract: ChargingContract | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !contract) {
    return <Skeleton className="h-40 w-full" />;
  }

  return (
    <DetailSection title="Contractgegevens" icon={FileText}>
      <FieldGrid
        fields={[
          { label: "Aanbieder", value: contract.providerName },
          { label: "Soort", value: contractTypeLabel(contract.contractType) },
          { label: "Contractnummer", value: contract.contractNumber },
          { label: "Accountnummer", value: contract.accountNumber },
          {
            label: "Tarief",
            value:
              contract.tariffPerKwh === null ||
              contract.tariffPerKwh === undefined
                ? undefined
                : formatTariffPerKwh(contract.tariffPerKwh),
          },
          { label: "Medewerker", value: contract.employeeName },
          {
            label: "Startdatum",
            value: contract.startAt
              ? formatDateOrEmpty(contract.startAt)
              : undefined,
          },
          {
            label: "Einddatum",
            value: contract.endAt
              ? formatDateOrEmpty(contract.endAt)
              : undefined,
          },
        ]}
      />
    </DetailSection>
  );
}

/**
 * Every card ever bound under this contract, active and historical.
 *
 * Deliberately queried by `chargingContract` rather than per card: the contract
 * summary on the list page only names one employee and one vehicle, so this is
 * where the rest of them have to be visible.
 */
function AssignmentsSection({
  contractId,
}: {
  readonly contractId: string;
}): ReactNode {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminChargingCardAssignments(
      { chargingContract: contractId, page },
      { query: { placeholderData: keepPreviousData } },
    );

  const totalItems = data?.totalItems ?? 0;

  return (
    <div className="space-y-4">
      <DetailSection title="Koppelingen" icon={CreditCard}>
        <Table>
          <THead>
            <Tr>
              <Th className="w-32">Soort</Th>
              <Th>Medewerker / voertuig</Th>
              <Th className="w-32">Vanaf</Th>
              <Th className="w-32">Tot</Th>
              <Th className="w-28">Status</Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <AssignmentRows
              isPending={isPending}
              isError={isError}
              members={data?.member}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </DetailSection>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function AssignmentRows({
  isPending,
  isError,
  members,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly ChargingCardAssignment[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-20" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-40" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-20" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-20" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={COLUMN_COUNT} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de koppelingen niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td
          colSpan={COLUMN_COUNT}
          className="py-10 text-center text-muted-foreground"
        >
          Nog geen medewerkers of voertuigen gekoppeld.
        </Td>
      </Tr>
    );
  }

  return members.map((assignment) => (
    <Tr key={assignment.id}>
      <Td>{assignmentTypeLabel(assignment.assignmentType)}</Td>
      <Td className="font-medium">
        <AssignmentSubject assignment={assignment} />
      </Td>
      <Td className="text-muted-foreground">
        {formatDateOrEmpty(assignment.startAt)}
      </Td>
      <Td className="text-muted-foreground">
        {formatDateOrEmpty(assignment.endAt)}
      </Td>
      <Td>
        {assignment.endAt ? (
          <Badge variant="neutral" size="sm">
            Beëindigd
          </Badge>
        ) : (
          <Badge variant="success" size="sm">
            Actief
          </Badge>
        )}
      </Td>
    </Tr>
  ));
}

function AssignmentSubject({
  assignment,
}: {
  readonly assignment: ChargingCardAssignment;
}): ReactNode {
  if (assignment.licensePlate) {
    return assignment.vehicle ? (
      <Link
        to="/vehicles/$vehicleId"
        params={{ vehicleId: assignment.vehicle }}
        search={{ tab: "gegevens" }}
        className="inline-flex rounded-sm transition-opacity hover:opacity-80"
      >
        <LicensePlate value={assignment.licensePlate} size="sm" />
      </Link>
    ) : (
      <LicensePlate value={assignment.licensePlate} size="sm" />
    );
  }

  if (assignment.employeeName) {
    return assignment.employeeName;
  }

  return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
}
